/**
 * Hybrid nurture / lead-capture widget — behavior for the email_captures CPT
 * 'hybrid_widget' style.
 *
 * Step 1 subscribes the visitor's email to ActiveCampaign through the shared
 * `email_capture_subscribe` AJAX action (a fresh nonce is fetched at submit
 * time so cached pages stay valid). On success it advances to Step 2 — a
 * FluentForm, when the capture defines one — otherwise straight to the success
 * state. The name is used only to personalize the Step 2 hand-off.
 *
 * Every .js-hybrid-lead-widget instance is bound and scoped to its own root so
 * multiple placements never collide.
 *
 * @file hybrid-lead-widget.js
 */
(() => {
  'use strict';

  /** Matches the CSS step transition duration so we swap after it finishes. */
  const TRANSITION_MS = 220;

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  // Class names shared with scss/organisms/_hybrid-lead-widget.scss — rename there too.
  const PREFILLED_GROUP_CLASS = 'is-prefilled';
  const PREFILL_PILL_CLASS    = 'c-hlw-prefill-pill';

  const EMAIL_SELECTOR     = 'input[name="email"], input[type="email"]';
  const FULL_NAME_SELECTOR = 'input[name="full_name"]';

  // FluentForm "Name Fields" element renders names_N[first_name] /
  // names_N[last_name] — the numeric N varies per form, so match by suffix.
  const FIRST_NAME_SELECTOR = 'input[name$="[first_name]"]';
  const LAST_NAME_SELECTOR  = 'input[name$="[last_name]"]';

  /** @typedef {{fullName: string, firstName: string, lastName: string, email: string, prefilledFullName: string, prefilledEmail: string}} LeadValues */

  /**
   * @param {string} fullName
   * @returns {{firstName: string, lastName: string}} Everything past the first token is the surname.
   */
  const splitFullName = (fullName) => {
    const nameParts = fullName.trim().split(/\s+/);
    return { firstName: nameParts[0] || '', lastName: nameParts.slice(1).join(' ') };
  };

  /**
   * Seed the lead state from Step 1. The prefilled* pair duplicates fullName and
   * email on purpose — frozen here, they are the only way a later read can tell
   * an untouched field from one the visitor retyped.
   *
   * @param {string} fullName - As captured by Step 1.
   * @param {string} email
   * @returns {LeadValues}
   */
  const leadValuesFromSignup = (fullName, email) => ({
    fullName,
    ...splitFullName(fullName),
    email,
    prefilledFullName: fullName,
    prefilledEmail: email,
  });

  /**
   * Rebuild the values from a persisted entry, filling in whatever an older
   * entry predates. A cleared first name is `''`; switching the parts to `||`
   * resurrects the derived value over it.
   *
   * @param {{[key: string]: *}} savedState
   * @returns {LeadValues}
   */
  const leadValuesFromState = (savedState) => {
    const fullName      = savedState.fullName || savedState.name || '';
    const derivedParts  = splitFullName(fullName);

    return {
      fullName,
      firstName: savedState.firstName ?? derivedParts.firstName,
      lastName: savedState.lastName ?? derivedParts.lastName,
      email: savedState.email || '',
      prefilledFullName: savedState.prefilledFullName || '',
      prefilledEmail: savedState.prefilledEmail || '',
    };
  };

  /** sessionStorage key namespace; progress is stored per capture id. */
  const STORAGE_PREFIX = 'hlw-state:';

  /**
   * Read this capture's persisted progress, or null. Lead-step entries carry a
   * LeadValues payload alongside `step`; entries from before that shape existed
   * carry a single `name` instead, which leadValuesFromState() absorbs.
   *
   * @param {string} captureId
   * @returns {?{step: string, [key: string]: *}}
   */
  const readState = (captureId) => {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_PREFIX + captureId);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  /**
   * Persist this capture's progress. Best-effort — silently no-ops when
   * sessionStorage is unavailable (private mode / disabled / quota).
   * @param {string} captureId
   * @param {{step: string, name?: string, email?: string}} state
   * @returns {void}
   */
  const writeState = (captureId, state) => {
    try {
      window.sessionStorage.setItem(STORAGE_PREFIX + captureId, JSON.stringify(state));
    } catch {
      return;
    }
  };

  /** localStorage key namespace for the "full lead completed" flag. */
  const DONE_PREFIX = 'hlw-done:';

  /** How long a completed lead is remembered before the widget shows again. */
  const DONE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  /**
   * Whether this capture's full lead was completed within the last DONE_TTL_MS
   * (FluentForm submitted). Stored per capture in localStorage; entries past the
   * window are treated as expired and pruned on read.
   * @param {string} captureId
   * @returns {boolean}
   */
  const readDone = (captureId) => {
    try {
      const raw = window.localStorage.getItem(DONE_PREFIX + captureId);
      if (!raw) return false;
      const { ts } = JSON.parse(raw);
      if (typeof ts !== 'number' || Date.now() - ts >= DONE_TTL_MS) {
        window.localStorage.removeItem(DONE_PREFIX + captureId);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  /**
   * Mark this capture's full lead complete, timestamped for the DONE_TTL_MS
   * window. Best-effort — no-ops when localStorage is unavailable.
   * @param {string} captureId
   * @returns {void}
   */
  const writeDone = (captureId) => {
    try {
      window.localStorage.setItem(DONE_PREFIX + captureId, JSON.stringify({ ts: Date.now() }));
    } catch {
      return;
    }
  };

  /**
   * Animate one step out and the next step in (mirrors the prototype timing).
   * Owns the display swap by toggling tw-hidden/tw-flex — steps ship hidden in
   * the markup, so JS supplies tw-flex when revealing — alongside the
   * opacity/translate utilities for the fade.
   *
   * @param {HTMLElement} fromStep
   * @param {HTMLElement} toStep
   * @returns {void}
   */
  const transitionStep = (fromStep, toStep) => {
    fromStep.classList.add('tw-opacity-0', '-tw-translate-y-2.5');

    window.setTimeout(() => {
      fromStep.classList.add('tw-hidden');
      fromStep.classList.remove('tw-flex', 'tw-opacity-0', '-tw-translate-y-2.5');

      toStep.classList.remove('tw-hidden');
      toStep.classList.add('tw-flex', 'tw-opacity-0', 'tw-translate-y-2.5');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          toStep.classList.remove('tw-opacity-0', 'tw-translate-y-2.5');
        });
      });
    }, TRANSITION_MS);
  };

  /**
   * Subscribe an email to this capture's ActiveCampaign list via WP AJAX.
   * Fetches a fresh nonce first so cached pages never post a stale one.
   *
   * @param {string} email
   * @param {string} captureId
   * @param {string} honeypotValue
   * @returns {Promise<{success: boolean, message: string}>} `message` carries the
   *   server's error text (e.g. the rate-limit notice) on failure, else ''.
   */
  const subscribeToActiveCampaign = async (email, captureId, honeypotValue) => {
    const ajaxUrl = window.EmailCaptureConfig?.ajaxUrl;
    if (!ajaxUrl) return { success: false, message: '' };

    const nonceRequest = new FormData();
    nonceRequest.append('action', 'get_email_capture_nonce');
    const nonceResponse = await fetch(ajaxUrl, { method: 'POST', body: nonceRequest });
    const nonceResult = await nonceResponse.json();

    const subscribeRequest = new FormData();
    subscribeRequest.append('action', 'email_capture_subscribe');
    subscribeRequest.append('nonce', nonceResult.data.nonce);
    subscribeRequest.append('email', email);
    subscribeRequest.append('capture_id', captureId);
    subscribeRequest.append('source_url', window.location.href);
    subscribeRequest.append('honeypot_value', honeypotValue);

    const subscribeResponse = await fetch(ajaxUrl, { method: 'POST', body: subscribeRequest });
    const subscribeResult = await subscribeResponse.json();

    return {
      success: subscribeResult.success === true,
      message: subscribeResult.data?.message || '',
    };
  };

  /**
   * Resolve the Step 2 inputs once. Both the fill and the read-back direction
   * take this, so the FluentForm markup contract lives in exactly one place.
   *
   * @param {HTMLElement} leadStep
   * @returns {{emailField: ?HTMLInputElement, fullNameField: ?HTMLInputElement, firstNameField: ?HTMLInputElement, lastNameField: ?HTMLInputElement}}
   */
  const resolveLeadFields = (leadStep) => ({
    emailField:     leadStep.querySelector(EMAIL_SELECTOR),
    fullNameField:  leadStep.querySelector(FULL_NAME_SELECTOR),
    firstNameField: leadStep.querySelector(FIRST_NAME_SELECTOR),
    lastNameField:  leadStep.querySelector(LAST_NAME_SELECTOR),
  });

  /**
   * Read the current values back out of the resolved inputs. The name comes back
   * as parts as well as combined — collapse it to the combined string alone and
   * restore re-splits it, shuffling values between the first and last inputs.
   *
   * @param {ReturnType<typeof resolveLeadFields>} fields
   * @returns {{fullName: string, firstName: string, lastName: string, email: string}}
   */
  const readLeadFields = ({ emailField, fullNameField, firstNameField, lastNameField }) => {
    const email = emailField?.value.trim() || '';

    if (firstNameField && lastNameField) {
      const firstName = firstNameField.value;
      const lastName  = lastNameField.value;
      return { fullName: [firstName, lastName].filter(Boolean).join(' ').trim(), firstName, lastName, email };
    }

    const fullName = (fullNameField || firstNameField)?.value.trim() || '';

    return { fullName, ...splitFullName(fullName), email };
  };

  /**
   * Add the "✓ from signup" pill into the field's label once, returning it.
   * Must stay a real element — as CSS generated content the text never reaches
   * assistive tech; the aria-hidden span keeps the checkmark glyph out of
   * what's announced.
   *
   * @param {HTMLInputElement} field
   * @returns {?HTMLElement} Null when the field has no FluentForm label.
   */
  const ensurePrefillPill = (field) => {
    const label = field.closest('.ff-el-group')?.querySelector('.ff-el-input--label label');
    if (!label) return null;

    let pill = label.querySelector(`.${PREFILL_PILL_CLASS}`);
    if (!pill) {
      pill = document.createElement('span');
      pill.className = PREFILL_PILL_CLASS;
      if (field.id) pill.id = `${field.id}-prefill-note`;

      const checkmark = document.createElement('span');
      checkmark.setAttribute('aria-hidden', 'true');
      checkmark.textContent = '✓ ';
      pill.append(checkmark, 'from signup');

      label.append(pill);
    }
    return pill;
  };

  /**
   * Tie the pill to the input via aria-describedby while flagged, untie it when
   * not. FluentForm's labels carry an aria-label that overrides their text
   * content, so this reference is the pill's only route to assistive tech —
   * and a hidden pill left referenced would still be announced.
   *
   * @param {HTMLInputElement} field
   * @param {boolean} isPrefilled
   * @returns {void}
   */
  const syncPillDescription = (field, isPrefilled) => {
    const pill = ensurePrefillPill(field);
    if (!pill?.id) return;

    const descriptionIds = (field.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((descriptionId) => descriptionId && descriptionId !== pill.id);
    if (isPrefilled) descriptionIds.push(pill.id);

    if (descriptionIds.length) {
      field.setAttribute('aria-describedby', descriptionIds.join(' '));
    } else {
      field.removeAttribute('aria-describedby');
    }
  };

  /**
   * Copy the Step 1 name + email into the Step 2 FluentForm so the visitor
   * doesn't retype them. Handles a single full-name input as well as
   * FluentForm's split first/last name element, firing input/change so the
   * form's own state registers the injected values.
   *
   * PREFILLED_GROUP_CLASS is a live per-field comparison against the frozen
   * Step 1 originals — flagged only while the field's value matches what signup
   * provided, in both directions (editing away clears it, matching again — even
   * retyped — restores it), so live edits and a refresh-restore always agree.
   *
   * @param {HTMLElement} leadStep - The Step 2 container holding the FluentForm.
   * @param {ReturnType<typeof resolveLeadFields>} fields
   * @param {LeadValues} values
   * @param {() => void} onEdit - Fires on every keystroke in a lead field.
   * @returns {void}
   */
  const prefillLeadForm = (leadStep, fields, values, onEdit) => {
    const { emailField, fullNameField, firstNameField, lastNameField } = fields;

    const prefilledParts = splitFullName(values.prefilledFullName);

    // The first-name entry mirrors the fill rule below: with no last-name input
    // that field holds the whole name.
    const frozenSignupValues = new Map([
      [emailField, values.prefilledEmail],
      [fullNameField, values.prefilledFullName],
      [firstNameField, lastNameField ? prefilledParts.firstName : values.prefilledFullName],
      [lastNameField, prefilledParts.lastName],
    ]);

    /**
     * Trimmed on both sides to match what persistence stores; the non-empty
     * guard keeps legacy entries (empty frozen values) from flagging an empty
     * field as "from signup".
     * @param {HTMLInputElement} field
     * @returns {boolean}
     */
    const fieldIsPrefilled = (field) => {
      const currentValue = field.value.trim();
      return currentValue !== '' && currentValue === (frozenSignupValues.get(field) || '').trim();
    };

    /** @param {HTMLInputElement} field @returns {void} */
    const refreshPrefilledState = (field) => {
      const isPrefilled = fieldIsPrefilled(field);
      field.closest('.ff-el-group')?.classList.toggle(PREFILLED_GROUP_CLASS, isPrefilled);
      syncPillDescription(field, isPrefilled);
    };

    /**
     * Empty values are assigned too — a restore must clear a field the visitor
     * cleared, or a FluentForm default would resurface over it.
     * @param {HTMLInputElement|null} field
     * @param {string} value
     */
    const setFieldValue = (field, value) => {
      if (!field) return;
      field.value = value;
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    };

    setFieldValue(emailField, values.email);
    setFieldValue(fullNameField, values.fullName);

    // With no last-name input the first-name field carries the whole name (it is
    // often just relabeled "Full Name").
    setFieldValue(firstNameField, lastNameField ? values.firstName : values.fullName);
    setFieldValue(lastNameField, values.lastName);

    const editableFields = [emailField, fullNameField, firstNameField, lastNameField].filter(Boolean);

    editableFields.forEach(refreshPrefilledState);

    // Subscribed after the fills above, so the synthetic events they dispatch
    // never reach it.
    leadStep.addEventListener('input', (inputEvent) => {
      if (!editableFields.includes(inputEvent.target)) return;
      refreshPrefilledState(inputEvent.target);
      onEdit();
    });
  };

  /**
   * @param {HTMLElement} widget - A single .js-hybrid-lead-widget root.
   * @returns {void}
   */
  const initWidget = (widget) => {
    const captureId  = widget.dataset.captureId;
    const successMsg = widget.dataset.successMsg || '';

    const stepNurture = widget.querySelector('[data-step="1"]');
    const stepLead    = widget.querySelector('[data-step="2"]');
    const stepSuccess = widget.querySelector('[data-step="success"]');
    const nurtureForm = widget.querySelector('.js-hlw-form-nurture');
    const errorEl     = widget.querySelector('.js-hlw-error');
    const skipButton  = widget.querySelector('.js-hlw-skip');
    const leadSubhead = widget.querySelector('.js-hlw-step2-subhead');
    const successBody = widget.querySelector('.js-hlw-success-body');

    if (!stepNurture || !stepSuccess || !nurtureForm) return;

    const nameInput     = nurtureForm.querySelector('input[name="full_name"]');
    const emailInput    = nurtureForm.querySelector('input[name="email"]');
    const honeypotInput = nurtureForm.querySelector('input[name="honeypot_value"]');
    const submitButton  = nurtureForm.querySelector('button[type="submit"]');

    // Required for the submit flow; the honeypot stays optional (anti-spam only).
    if (!nameInput || !emailInput || !submitButton) return;

    /** @param {string} message @returns {void} */
    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.classList.remove('tw-hidden');
    };

    /** @param {HTMLElement} fromStep @returns {void} */
    const advanceToSuccess = (fromStep) => {
      if (successBody) successBody.textContent = successMsg;
      writeState(captureId, { step: 'success' });
      transitionStep(fromStep, stepSuccess);
    };

    /**
     * Prefill + personalize Step 2. Shared by the live advance and the
     * refresh-restore path so both render identically.
     *
     * Persists every edit so a refresh restores what the visitor last typed.
     *
     * @param {LeadValues} values
     * @returns {void}
     */
    const populateLeadStep = (values) => {
      const fields = resolveLeadFields(stepLead);

      prefillLeadForm(stepLead, fields, values, () => {
        writeState(captureId, {
          step: 'lead',
          ...readLeadFields(fields),
          prefilledFullName: values.prefilledFullName,
          prefilledEmail: values.prefilledEmail,
        });
      });

      // A cleared-then-persisted first name keeps the template's generic subhead.
      const greetingName = values.firstName.trim();
      if (leadSubhead && greetingName) {
        leadSubhead.textContent = `Thanks, ${greetingName}. Add a few details and we'll connect you with an expert within one business day.`;
      }
    };

    /**
     * Snap straight to a step with no fade — restores persisted progress on load
     * without replaying the transition animation.
     * @param {HTMLElement} step
     * @returns {void}
     */
    const snapToStep = (step) => {
      [stepNurture, stepLead, stepSuccess].forEach((candidate) => {
        if (!candidate) return;
        const isTarget = candidate === step;
        candidate.classList.toggle('tw-hidden', !isTarget);
        candidate.classList.toggle('tw-flex', isTarget);
        candidate.classList.remove('tw-opacity-0', 'tw-translate-y-2.5', '-tw-translate-y-2.5');
      });
    };

    /** @param {SubmitEvent} submitEvent @returns {Promise<void>} */
    /**
     * Step 1 posts through its own fetch and carries no js-validateForm, so the shared form
     * pipeline never sees it. Step 2 is an embedded FluentForm and is already covered by
     * fluent-form-to-formparser.js.
     *
     * @param {string} action - Submit, Success, Error or Skip.
     * @param {number} step - 1 for the nurture step, 2 for the lead step.
     * @param {string} [reason] - Failure detail; Mixpanel only.
     */
    const trackWidget = (action, step, reason) => {
      if (typeof window.customEvent !== 'function') {
        return;
      }

      window.customEvent('Form', {
        action: action,
        step: step,
        title: nurtureForm.dataset.trackTitle,
        location: widget.closest('[data-location]')?.dataset.location || 'hybrid-lead-widget',
        offer: nurtureForm.dataset.trackOffer || `hybrid-lead-widget:${captureId}`,
        reason: reason,
        microIgnore: false,
        nonInteraction: false
      });
    };

    const handleNurtureSubmit = async (submitEvent) => {
      submitEvent.preventDefault();

      const fullName = nameInput.value.trim();
      const email    = emailInput.value.trim();

      if (!fullName || !EMAIL_PATTERN.test(email)) {
        emailInput.setAttribute('aria-invalid', 'true');
        showError('Please enter your name and a valid email address.');
        trackWidget('Error', 1, 'Invalid Name Or Email');
        return;
      }

      emailInput.removeAttribute('aria-invalid');
      errorEl?.classList.add('tw-hidden');
      trackWidget('Submit', 1);

      const originalLabel = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.textContent = 'Submitting…';

      let subscription = { success: false, message: '' };
      try {
        subscription = await subscribeToActiveCampaign(email, captureId, honeypotInput?.value || '');
      } catch {
        subscription = { success: false, message: '' };
      }

      if (!subscription.success) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        submitButton.textContent = originalLabel;
        const failureMessage = subscription.message || 'Something went wrong. Please try again.';
        trackWidget('Error', 1, failureMessage);
        showError(failureMessage);
        return;
      }

      trackWidget('Success', 1);

      // Advance: to the FluentForm step when configured, else straight to success.
      if (stepLead) {
        const leadValues = leadValuesFromSignup(fullName, email);
        populateLeadStep(leadValues);
        writeState(captureId, { step: 'lead', ...leadValues });
        transitionStep(stepNurture, stepLead);
      } else {
        advanceToSuccess(stepNurture);
      }
    };

    nurtureForm.addEventListener('submit', handleNurtureSubmit);

    // The skip link only renders inside Step 2, so stepLead is always present here.
    skipButton?.addEventListener('click', () => {
      trackWidget('Skip', 2);
      advanceToSuccess(stepLead);
    });

    // When the embedded FluentForm is submitted, mark the lead fully complete in
    // localStorage (persists across sessions) and show our thank-you — so a return
    // visit shows the confirmation, not the form again. FluentForm fires this event
    // on its own <form> and ships jQuery.
    const leadForm = stepLead ? stepLead.querySelector('form') : null;
    if (window.jQuery && leadForm) {
      window.jQuery(leadForm).on('fluentform_submission_success', () => {
        writeDone(captureId);
        advanceToSuccess(stepLead);
      });
    }

    // Restore prior progress on load — never re-subscribes, only re-snaps the DOM:
    //   completed lead (localStorage) → thank-you, across sessions
    //   Step 1 done (sessionStorage)  → Step 2 prefilled, this session only
    if (readDone(captureId)) {
      if (successBody) successBody.textContent = successMsg;
      snapToStep(stepSuccess);
    } else {
      const savedState = readState(captureId);
      if (savedState?.step === 'lead' && stepLead) {
        populateLeadStep(leadValuesFromState(savedState));
        snapToStep(stepLead);
      } else if (savedState?.step === 'success') {
        if (successBody) successBody.textContent = successMsg;
        snapToStep(stepSuccess);
      }
    }
  };

  document.querySelectorAll('.js-hybrid-lead-widget').forEach(initWidget);
})();
